import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, DatePicker, Dropdown, Empty, Input, InputNumber, Modal, Popconfirm, Select, Switch, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowDown, ArrowLeft, ArrowUp, Award, BadgeCheck, Briefcase, Building, Calendar, CalendarCheck, Check, CheckSquare, ChevronDown,
  ChevronRight, ClipboardList, CreditCard, FileSignature, GraduationCap, HardHat, IdCard, KeyRound, Laptop, LogOut, Mail, MoreHorizontal,
  Package, PauseCircle, Pencil, Phone, Shirt, ShieldCheck, ShieldAlert, User, UserCheck,
} from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr, type Result } from './hrStore';
import {
  ASSET_KIND, CONTRACT_KIND, CSTATE, DISCIPLINE_LEVELS, DOC_KEYS, EDUCATION, FIXED_MAX_TIMES, INS_DEADLINE_DAYS, INS_STATE, LEAVE_TYPE,
  LEVEL, LICENSE_KIND, MARITAL, PHASE, SCHEDULES, SOURCE, addDays, branchById, contractState, currentContract, daysBetween,
  deptById, deptOf, empById, empContracts, fixedCount, fmtD, fmtM, fmtMoney, healthState, insState, isWorking, licenseState, normText,
  officialStart, phase, LEAVE_KIND, leaveBalance, MOVE_TYPE, DEP_RELATIONS, activeDependents, salaryAt, validateMove, type Dependent, type MoveDraft, type MoveType, positionById, reqById, requiredDocs, tenureText, toDay, unreturnedAssets,
  type AssetItem, type AssetKind, type Contract, type DocKey, type Education, type Employee, type License, type LicenseKind,
  type Phase, type RecordItem, type RecordKind, type School, type WorkItem,
} from './hrLogic';
import { actionItems } from './hrInsights';
import { Badge, Field, LevelDot, Notes, avatarColor, candUrl, empUrl, fmtDateTime, initials, reqUrl, useHrPerms } from './hrUi';
import { ContractModal, DateInput, EvalModal, LeaveModal, MoneyInput, OffboardModal } from './hrModals';
import { NoShowModal } from './RecruitPanels';
import { noShowCheck } from './hrLogic';

/* ============================== Khối giao diện kiểu cũ ============================== */

const TH = 'py-3 px-4 text-slate-400 dark:text-slate-500 font-semibold text-[11px] uppercase tracking-wide';
const TR = 'border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors';

type Link = { label: React.ReactNode; onClick: () => void; danger?: boolean } | false | null | undefined;
function Links({ items }: { items: Link[] }) {
  const list = items.filter(Boolean) as Exclude<Link, false | null | undefined>[];
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[13px] font-medium text-[#3b8e9d] dark:text-teal-300">
      {list.map((l, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-300 dark:text-slate-600">|</span>}
          <button type="button" onClick={l.onClick} className={`hover:underline ${l.danger ? 'text-rose-600' : ''}`}>{l.label}</button>
        </React.Fragment>
      ))}
    </div>
  );
}

function Sec({ title, sub, links, children, first }: { title: string; sub: string; links?: Link[]; children: React.ReactNode; first?: boolean }) {
  return (
    <div className={first ? '' : 'border-t border-slate-200 dark:border-slate-700 pt-8'}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h4 className="text-[15px] font-semibold text-slate-800 dark:text-slate-100 mb-1">{title}</h4>
          <p className="text-sm text-slate-500 dark:text-slate-400">{sub}</p>
        </div>
        {links && <Links items={links} />}
      </div>
      {children}
    </div>
  );
}

const TabHead = ({ title, sub, links, green }: { title: string; sub: string; links?: Link[]; green?: boolean }) => (
  <div className="flex justify-between items-start sm:flex-row flex-col gap-2">
    <div>
      <h3 className={`text-lg font-semibold mb-1 ${green ? 'text-[#3b8e9d] dark:text-teal-300' : 'text-slate-800 dark:text-slate-100'}`}>{title}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">{sub}</p>
    </div>
    {links && <Links items={links} />}
  </div>
);

/** Ô thông tin: nhãn + giá trị; tone xanh cho giá trị dạng danh mục như mẫu cũ */
function F({ label, children, blue, icon, hint, warn }: { label: string; children?: React.ReactNode; blue?: boolean; icon?: React.ReactNode; hint?: React.ReactNode; warn?: boolean }) {
  const empty = children === undefined || children === null || children === '';
  return (
    <div className="min-w-0">
      <div className="text-[12px] text-slate-400 dark:text-slate-500 mb-1">{label}</div>
      <div className={`text-sm flex items-center break-words ${empty ? 'font-normal text-slate-400' : warn ? 'font-medium text-rose-600' : blue ? 'font-medium text-[#3b8e9d] dark:text-teal-300' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
        {!empty && icon}{empty ? '--' : children}
      </div>
      {hint && <div className="text-[11.5px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

const RowActions = ({ items }: { items: { key: string; label: string; danger?: boolean; onClick: () => void }[] }) => (
  <div className="flex border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden w-fit">
    <button type="button" onClick={items[0].onClick} className="px-3 py-1.5 text-[13px] font-semibold border-r border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-white">{items[0].label}</button>
    <Dropdown trigger={['click']} disabled={items.length < 2} menu={{ items: items.slice(1).map(i => ({ key: i.key, label: i.label, danger: i.danger })), onClick: ({ key }) => items.find(i => i.key === key)?.onClick() }}>
      <button type="button" className="px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-white disabled:opacity-40"><ChevronDown size={14} /></button>
    </Dropdown>
  </div>
);

const EmptyRow = ({ cols, text }: { cols: number; text: string }) => <tr><td colSpan={cols} className="p-8 text-center text-sm text-slate-400">{text}</td></tr>;

/* ============================== Form sửa nhanh theo nhóm ============================== */

interface FieldDef { key: string; label: string; type?: 'text' | 'date' | 'month' | 'number' | 'money' | 'select' | 'textarea' | 'check'; options?: { value: string; label: string }[]; span?: boolean; placeholder?: string }
interface EditState { title: string; fields: FieldDef[]; values: Record<string, unknown>; onSave: (v: Record<string, unknown>) => Result }

function EditModal({ edit, onClose }: { edit: EditState; onClose: () => void }) {
  const [v, setV] = useState<Record<string, unknown>>(edit.values);
  const set = (k: string, x: unknown) => setV(p => ({ ...p, [k]: x }));
  const save = () => {
    const r = edit.onSave(v);
    if (!r.ok) { Modal.error({ title: 'Chưa lưu được', content: r.errors?.join('\n') }); return; }
    message.success('Đã lưu');
    onClose();
  };
  return (
    <Modal open title={edit.title} onCancel={onClose} onOk={save} okText="Lưu" cancelText="Đóng" width={620}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {edit.fields.map(f => (
          <Field key={f.key} label={f.label} className={f.span || f.type === 'textarea' ? 'sm:col-span-2' : ''}>
            {f.type === 'date' ? <DateInput value={(v[f.key] as string) || undefined} onChange={x => set(f.key, x || undefined)} />
              : f.type === 'month' ? <DatePicker picker="month" className="w-full" format="MM/YYYY" placeholder="Chọn tháng" value={v[f.key] ? dayjs(`${v[f.key]}-01`) : null} onChange={x => set(f.key, x ? x.format('YYYY-MM') : undefined)} />
              : f.type === 'money' ? <MoneyInput value={(v[f.key] as number) || 0} onChange={x => set(f.key, x || undefined)} />
                : f.type === 'number' ? <InputNumber className="!w-full" min={0} value={(v[f.key] as number) ?? null} onChange={x => set(f.key, x ?? undefined)} />
                  : f.type === 'select' ? <Select className="w-full" allowClear value={(v[f.key] as string) || undefined} onChange={x => set(f.key, x)} options={f.options} showSearch optionFilterProp="label" />
                    : f.type === 'textarea' ? <Input.TextArea rows={3} value={(v[f.key] as string) || ''} onChange={x => set(f.key, x.target.value)} />
                      : f.type === 'check' ? <Checkbox checked={!!v[f.key]} onChange={x => set(f.key, x.target.checked)}>{f.placeholder}</Checkbox>
                        : <Input value={(v[f.key] as string) || ''} placeholder={f.placeholder} onChange={x => set(f.key, x.target.value)} />}
          </Field>
        ))}
      </div>
    </Modal>
  );
}

/** Quyết định điều chuyển / bổ nhiệm / luân chuyển / điều chỉnh lương */
export function MoveModal({ e, initType, onClose }: { e: Employee; initType: MoveType; onClose: () => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const [d, setD] = useState<MoveDraft>({ type: initType, date: today, toPositionId: e.positionId, toBranchId: e.branchId, salary: undefined, reason: '' });
  const patch = (p: Partial<MoveDraft>) => setD(x => ({ ...x, ...p }));
  const v = validateMove(s, e, d, today);
  const cur = salaryAt(s, e, today);
  const to = positionById(s, d.toPositionId);
  const save = () => {
    const r = hrApi.moveEmployee(e.id, { ...d, reason: d.reason?.trim() || undefined }, me);
    if (!r.ok) { Modal.error({ title: 'Chưa lập được quyết định', content: r.errors?.join('\n') }); return; }
    message.success(d.date > today ? `Đã lập quyết định — tự áp dụng ngày ${fmtD(d.date)}` : 'Đã lập quyết định và cập nhật hồ sơ');
    onClose();
  };
  return (
    <Modal open title={`Lập quyết định · ${e.name}`} onCancel={onClose} width={640}
      footer={[<Button key="c" onClick={onClose}>Đóng</Button>, <Button key="s" type="primary" disabled={v.errors.length > 0} onClick={save}>Lập quyết định</Button>]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 text-xs text-slate-500 bg-slate-50 dark:bg-slate-800/60 rounded-lg px-3 py-2">
          Hiện tại: <b className="font-medium text-slate-700 dark:text-slate-200">{positionById(s, e.positionId)?.name}</b> · {branchById(s, e.branchId)?.name} · lương {fmtMoney(cur)}
        </div>
        <Field label="Loại quyết định" className="sm:col-span-2">
          <Select className="w-full" value={d.type} onChange={type => patch({ type, ...(type === 'salary' ? { toPositionId: e.positionId, toBranchId: e.branchId } : {}) })}
            options={(Object.keys(MOVE_TYPE) as MoveType[]).map(k => ({ value: k, label: MOVE_TYPE[k].label }))} />
        </Field>
        {d.type !== 'salary' && (
          <>
            <Field label="Chức danh mới">
              <Select showSearch className="w-full" value={d.toPositionId} optionFilterProp="label" onChange={toPositionId => patch({ toPositionId })}
                options={s.depts.map(dp => ({ label: dp.name, options: s.positions.filter(p => p.deptId === dp.id).map(p => ({ value: p.id, label: p.name })) }))} />
            </Field>
            <Field label="Chi nhánh">
              <Select className="w-full" value={d.toBranchId} onChange={toBranchId => patch({ toBranchId })} options={s.branches.map(b => ({ value: b.id, label: b.name }))} />
            </Field>
          </>
        )}
        <Field label="Ngày hiệu lực" hint="Quyết định tương lai sẽ tự áp vào hồ sơ khi tới ngày"><DateInput value={d.date} onChange={date => patch({ date })} min={e.hireDate} /></Field>
        {d.type === 'rotation' ? <Field label="Đến ngày (hết luân chuyển)"><DateInput value={d.until} onChange={until => patch({ until: until || undefined })} min={d.date} /></Field>
          : <Field label="Số quyết định" hint="Bỏ trống để hệ thống tự cấp"><Input value={d.decisionNo} onChange={x => patch({ decisionNo: x.target.value })} placeholder="VD: QĐ2610-015" /></Field>}
        <Field label={d.type === 'salary' ? 'Mức lương mới' : 'Lương mới (nếu thay đổi)'} hint={to ? `Khung ${to.name}: ${fmtM(to.salaryMin)} – ${fmtM(to.salaryMax)}` : undefined}>
          <MoneyInput value={d.salary} onChange={salary => patch({ salary: salary || undefined })} placeholder={fmtMoney(cur)} />
        </Field>
        <Field label="Lý do" className="sm:col-span-2"><Input.TextArea rows={2} value={d.reason} onChange={x => patch({ reason: x.target.value })} placeholder="VD: Hoàn thành tốt nhiệm vụ, bổ sung nhân sự chi nhánh…" /></Field>
        <div className="sm:col-span-2"><Notes errors={v.errors} warnings={v.warnings} /></div>
        {d.salary && d.salary !== cur && <div className="sm:col-span-2 text-[11px] text-slate-400">Thay đổi lương cần lập phụ lục hợp đồng ký với người lao động.</div>}
      </div>
    </Modal>
  );
}

const opts = <T extends string>(rec: Record<T, string>) => (Object.keys(rec) as T[]).map(k => ({ value: k, label: rec[k] }));
const clean = (v: Record<string, unknown>) => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, typeof x === 'string' ? x.trim() || undefined : x]));

/* ============================== Trang ============================== */

const PHASE_DOT: Record<Phase, string> = {
  upcoming: 'bg-indigo-500', probation: 'bg-amber-500', fixed: 'bg-emerald-500', indefinite: 'bg-emerald-500', ctv: 'bg-slate-400', nocontract: 'bg-rose-500', paused: 'bg-violet-500', left: 'bg-slate-400',
};
const PHASE_TEXT: Record<Phase, string> = {
  upcoming: 'text-indigo-600', probation: 'text-amber-600', fixed: 'text-emerald-500', indefinite: 'text-emerald-500', ctv: 'text-slate-500', nocontract: 'text-rose-600', paused: 'text-violet-600', left: 'text-slate-500',
};
const ASSET_ICON: Record<AssetKind, { icon: React.ReactNode; cls: string }> = {
  device: { icon: <Laptop size={20} />, cls: 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300' },
  uniform: { icon: <Shirt size={20} />, cls: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  card: { icon: <KeyRound size={20} />, cls: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  tool: { icon: <HardHat size={20} />, cls: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  other: { icon: <Package size={20} />, cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};

type TabKey = 'info' | 'extra' | 'contracts' | 'docs' | 'assets' | 'records' | 'log';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'info', label: 'Thông tin cá nhân' }, { key: 'extra', label: 'Thông tin bổ sung' }, { key: 'contracts', label: 'Hợp đồng & Lương' },
  { key: 'docs', label: 'Giấy phép & hồ sơ' }, { key: 'assets', label: 'Tài sản cấp phát' }, { key: 'records', label: 'Khen thưởng & vi phạm' }, { key: 'log', label: 'Lịch sử' },
];

/** Sự kiện sự nghiệp suy ra từ chuỗi hợp đồng */
function careerEvent(c: Contract, prev: Contract | undefined) {
  if (c.kind === 'probation') return { tag: 'TUYỂN DỤNG', tone: 'text-amber-600' };
  if (c.kind === 'service') return { tag: 'CỘNG TÁC', tone: 'text-slate-500' };
  const prevPay = prev ? (prev.kind === 'probation' ? prev.officialSalary || prev.salary : prev.salary) : 0;
  if (!prev || prev.kind === 'probation') return { tag: 'CHÍNH THỨC', tone: 'text-emerald-600' };
  if (c.salary > prevPay) return { tag: 'TĂNG LƯƠNG', tone: 'text-emerald-600' };
  if (c.kind === 'indefinite') return { tag: 'KHÔNG XĐ THỜI HẠN', tone: 'text-emerald-600' };
  return { tag: 'TÁI KÝ', tone: 'text-sky-600' };
}

function Detail() {
  const s = useHr();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { me, canEdit } = useHrPerms();
  const today = toDay();
  const e = empById(s, params.get('id') || undefined);
  const rawTab = params.get('tab');
  const tab: TabKey = TABS.some(t => t.key === rawTab) ? (rawTab as TabKey) : 'info';
  const [contractInit, setContractInit] = useState<Record<string, unknown> | null>(() => (params.get('newContract') ? {} : null));
  const [evalFor, setEvalFor] = useState<string | null>(null);
  const [offboard, setOffboard] = useState<null | 'voluntary' | 'discipline'>(null);
  const [noShow, setNoShow] = useState(false);
  const [lic, setLic] = useState<(Partial<License> & { kind: LicenseKind }) | null>(null);
  const [pause, setPause] = useState<{ from: string; to?: string; reason: string } | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [showInsLog, setShowInsLog] = useState(false);
  const [move, setMove] = useState<MoveType | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);

  if (!e) {
    return <Layout><Empty className="py-16" description="Không tìm thấy nhân sự"><Button onClick={() => navigate('/personnel-management')}>Về danh sách</Button></Empty></Layout>;
  }
  const pos = positionById(s, e.positionId);
  const dept = deptById(s, deptOf(s, e));
  const branch = branchById(s, e.branchId);
  const ph = phase(s, e, today);
  const contracts = empContracts(s, e.id);
  const cur = currentContract(s, e.id, today) || contracts[contracts.length - 1];
  const acts = actionItems(s, today).filter(a => a.empId === e.id);
  const ins = insState(s, e, today);
  const licState = licenseState(s, e, today);
  const health = healthState(e, today);
  const manager = empById(s, e.managerId);
  const req = reqById(s, e.requisitionId);
  const off = s.offboardings.filter(o => o.empId === e.id && o.status !== 'cancelled').pop();
  const probation = contracts.find(c => c.kind === 'probation' && !c.evaluation && c.start <= today);
  const working = ph !== 'left';
  const endDate = e.leftDate && e.leftDate < today ? e.leftDate : today;
  const official = officialStart(s, e.id);
  const pending = unreturnedAssets(e);
  const setTab = (t: TabKey) => { const n = new URLSearchParams(params); n.set('tab', t); n.delete('newContract'); setParams(n, { replace: true }); };
  const run = (r: Result, ok: string) => {
    if (r.ok) { message.success(ok); return true; }
    Modal.error({ title: 'Chưa thực hiện được', content: r.errors?.join('\n') });
    return false;
  };
  const profile = (title: string, fields: FieldDef[], what: string, map?: (v: Record<string, unknown>) => Partial<Employee>) =>
    setEdit({
      title, fields,
      values: Object.fromEntries(fields.map(f => [f.key, f.key.includes('.') ? f.key.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown> | undefined)?.[k], e) : (e as unknown as Record<string, unknown>)[f.key]])),
      onSave: v => hrApi.saveProfile(e.id, map ? map(clean(v)) : (clean(v) as Partial<Employee>), me, what),
    });
  const listEdit = <T extends { id: string }>(title: string, list: 'schools' | 'workHistory' | 'assets' | 'records' | 'dependentList', fields: FieldDef[], item: Partial<T>, fix?: (v: Record<string, unknown>) => Record<string, unknown>) =>
    setEdit({
      title, fields, values: item as Record<string, unknown>,
      onSave: v => hrApi.saveItem(e.id, list, { ...(fix ? fix(clean(v)) : clean(v)), id: item.id } as never, me),
    });

  /* ----- Lương & sự nghiệp ----- */
  const paid = (c: Contract) => c.salary;
  const firstOfficial = contracts[0] ? (contracts[0].kind === 'probation' ? contracts[0].officialSalary || contracts[0].salary : contracts[0].salary) : 0;
  const nowSalary = salaryAt(s, e, endDate);
  const years = dayjs(endDate).diff(dayjs(e.hireDate), 'month', true) / 12;
  const yearly = firstOfficial && years >= 1 && cur && cur.kind !== 'probation' ? Math.round((Math.pow(nowSalary / firstOfficial, 1 / years) - 1) * 1000) / 10 : null;
  const moves = [...(e.moves || [])].sort((a, b) => a.date.localeCompare(b.date));
  const pendingMoves = moves.filter(m => !m.applied);
  const series = [
    ...contracts.map(c => ({ t: c.start, salary: paid(c) })),
    ...moves.filter(m => m.applied && m.salary).map(m => ({ t: m.date, salary: m.salary! })),
    ...(cur ? [{ t: endDate, salary: nowSalary }] : []),
  ].filter(p => p.t <= endDate).sort((a, b) => a.t.localeCompare(b.t)).map(p => ({ ...p, label: dayjs(p.t).format('MM/YY') }));
  const deps = activeDependents(e, today);
  const leave = leaveBalance(s, e, today);
  const leaves = [...(e.leaveRecords || [])].filter(r => r.to.slice(0, 4) >= today.slice(0, 4)).sort((a, b) => b.from.localeCompare(a.from));

  /* ----- Thanh tác vụ ----- */
  const menuItems = [
    working && { key: 'contract', label: 'Lập hợp đồng / gia hạn', icon: <FileSignature size={14} /> },
    probation && { key: 'eval', label: 'Đánh giá thử việc', icon: <UserCheck size={14} /> },
    working && ph !== 'upcoming' && { key: 'move', label: 'Điều chuyển / bổ nhiệm', icon: <Briefcase size={14} /> },
    working && ph !== 'upcoming' && { key: 'salary', label: 'Điều chỉnh lương', icon: <ArrowUp size={14} /> },
    working && ph !== 'upcoming' && ph !== 'ctv' && { key: 'leave-rec', label: 'Ghi nhận nghỉ phép', icon: <Calendar size={14} /> },
    working && ph !== 'upcoming' && !e.paused && { key: 'pause', label: 'Tạm hoãn hợp đồng', icon: <PauseCircle size={14} /> },
    e.paused && working && { key: 'unpause', label: 'Kết thúc tạm hoãn', icon: <PauseCircle size={14} /> },
    { key: 'form', label: 'Sửa toàn bộ hồ sơ', icon: <Pencil size={14} /> },
    noShowCheck(s, e, today).ok && { key: 'noshow', label: 'Không đến nhận việc', icon: <LogOut size={14} />, danger: true },
    working && !e.leftDate && { key: 'leave', label: 'Lập thủ tục nghỉ việc', icon: <LogOut size={14} />, danger: true },
    off && { key: 'off', label: 'Xem thủ tục nghỉ việc', icon: <LogOut size={14} /> },
  ].filter(Boolean) as { key: string; label: string; icon: React.ReactNode; danger?: boolean }[];
  const onMenu = (key: string) => {
    if (key === 'contract') setContractInit({});
    if (key === 'eval' && probation) setEvalFor(probation.id);
    if (key === 'move') setMove('transfer');
    if (key === 'salary') setMove('salary');
    if (key === 'leave-rec') setLeaveOpen(true);
    if (key === 'pause') setPause({ from: today, reason: 'Nghỉ thai sản' });
    if (key === 'unpause') { hrApi.pause(e.id, undefined, me); message.success('Đã kết thúc tạm hoãn'); }
    if (key === 'form') navigate(`/personnel-management/add?id=${e.id}`);
    if (key === 'leave') setOffboard('voluntary');
    if (key === 'noshow') setNoShow(true);
    if (key === 'off' && off) navigate(`/offboarding?id=${off.id}`);
  };

  /* ============================== Tab: Thông tin cá nhân ============================== */
  const insLog = e.log.filter(l => /BHXH|bảo hiểm|thuế/i.test(l.text));
  const renderInfo = () => (
    <div className="space-y-10 pb-10">
      <TabHead green title="Thông tin cá nhân" sub="Các thông tin cá nhân, liên hệ, học vấn và lịch sử làm việc" />

      <Sec title="Thông tin chính" sub="Các thông tin cá nhân quan trọng" links={canEdit ? [
        { label: 'Sửa thông tin cơ bản', onClick: () => profile('Sửa thông tin cơ bản', [
          { key: 'name', label: 'Họ và tên' }, { key: 'gender', label: 'Giới tính', type: 'select', options: [{ value: 'Nữ', label: 'Nữ' }, { value: 'Nam', label: 'Nam' }] },
          { key: 'dob', label: 'Ngày sinh', type: 'date' }, { key: 'marital', label: 'Tình trạng hôn nhân', type: 'select', options: opts(MARITAL) },
          { key: 'phone', label: 'Số điện thoại' }, { key: 'email', label: 'Email' },
        ], 'thông tin cơ bản') },
        { label: 'Sửa thông tin công việc', onClick: () => setEdit({
          title: 'Sửa thông tin công việc', values: { managerId: e.managerId, schedule: e.schedule, education: e.education, hireDate: e.hireDate },
          fields: [
            { key: 'managerId', label: 'Quản lý trực tiếp', type: 'select', options: s.employees.filter(x => x.id !== e.id && isWorking(x, today) && positionById(s, x.positionId)?.level !== 'staff').map(x => ({ value: x.id, label: `${x.name} · ${positionById(s, x.positionId)?.name}` })) },
            { key: 'schedule', label: 'Lịch làm việc', type: 'select', options: SCHEDULES.map(x => ({ value: x, label: x })) },
            { key: 'education', label: 'Trình độ', type: 'select', options: opts(EDUCATION) },
            { key: 'hireDate', label: 'Ngày bắt đầu', type: 'date' },
          ],
          onSave: v => {
            const r = hrApi.saveEmployee({ ...e, managerId: (v.managerId as string) || undefined, education: (v.education as Education) || e.education, hireDate: String(v.hireDate || e.hireDate) }, me);
            if (r.ok && v.schedule !== e.schedule) hrApi.saveProfile(e.id, { schedule: v.schedule as string }, me, 'lịch làm việc');
            return r;
          },
        }) },
      ] : undefined} first>
        {pendingMoves.length > 0 && (
          <div className="mb-6 rounded-lg border border-sky-100 bg-sky-50/70 dark:bg-sky-900/20 dark:border-sky-900 px-4 py-2.5 text-[13px] text-sky-800 dark:text-sky-200">
            {pendingMoves.map(m => (
              <div key={m.id}>Quyết định <b className="font-medium">{m.decisionNo}</b> · {MOVE_TYPE[m.type].label}: {positionById(s, m.toPositionId)?.name} · {branchById(s, m.toBranchId)?.short} — hiệu lực {fmtD(m.date)}{m.until ? ` đến ${fmtD(m.until)}` : ''}. Hồ sơ sẽ tự cập nhật khi tới ngày.</div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-y-8 gap-x-6">
          <F label="Họ và tên">{e.name}</F>
          <F label="Mã nhân sự">{e.code}</F>
          <F label="Ngày bắt đầu">{fmtD(e.hireDate)}</F>
          <F label="Ngày chính thức" hint={!official && ph === 'probation' && cur?.end ? `Thử việc đến ${fmtD(cur.end)}` : undefined}>{official ? fmtD(official) : null}</F>

          <F label="Tình trạng việc làm"><span className={PHASE_TEXT[ph]}>{PHASE[ph].label}</span></F>
          <F label="Hợp đồng hiện tại" blue hint={cur?.end ? `đến ${fmtD(cur.end)}` : cur ? 'không thời hạn' : undefined}>
            {cur ? <button type="button" className="hover:underline text-left" onClick={() => setTab('contracts')}>{cur.code} · {CONTRACT_KIND[cur.kind].short}</button> : null}
          </F>
          <F label="Số điện thoại">{e.phone}</F>
          <F label="Chức danh">{pos?.name}</F>

          <F label="Ngày sinh" hint={e.dob ? `${dayjs(today).diff(dayjs(e.dob), 'year')} tuổi` : undefined}>{e.dob ? fmtD(e.dob) : null}</F>
          <F label="Email">{e.email}</F>
          <F label="Giới tính" icon={<User size={14} className={`${e.gender === 'Nữ' ? 'text-pink-500' : 'text-sky-500'} mr-1.5`} />}>{e.gender}</F>
          <F label="Tình trạng hôn nhân">{e.marital ? MARITAL[e.marital] : null}</F>

          <F label="Văn phòng" blue icon={<Building size={14} className="mr-1.5" />}>{branch?.name}</F>
          <F label="Lịch làm việc" blue icon={<Calendar size={14} className="mr-1.5" />}>{e.schedule}</F>
          <F label="Khu vực / Chuyên môn" blue icon={<CheckSquare size={14} className="mr-1.5" />}>{dept?.name}</F>
          <F label="Phân loại nhân sự" blue icon={<Briefcase size={14} className="mr-1.5" />}>{pos ? `${LEVEL[pos.level]} · ${ph === 'ctv' ? 'Cộng tác viên' : 'Toàn thời gian'}` : null}</F>

          <F label="Quản lý trực tiếp" blue>{manager ? <button type="button" className="hover:underline" onClick={() => navigate(empUrl(manager.id))}>{manager.name}</button> : null}</F>
          <F label="Trình độ">{EDUCATION[e.education]}</F>
          <F label="Nguồn tuyển" hint={req ? <button type="button" className="text-[#3b8e9d] hover:underline" onClick={() => navigate(reqUrl(req.id))}>Yêu cầu {req.code}</button> : undefined}>{e.source ? SOURCE[e.source] : null}</F>
          <F label="Thâm niên">{tenureText(e.hireDate, endDate)}</F>
        </div>
      </Sec>

      <Sec title="Quá trình công tác" sub="Bổ nhiệm, điều chuyển, luân chuyển và điều chỉnh lương theo quyết định" links={canEdit && working ? [
        { label: 'Điều chuyển / bổ nhiệm', onClick: () => setMove('transfer') },
        { label: 'Điều chỉnh lương', onClick: () => setMove('salary') },
      ] : undefined}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left border-collapse">
            <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
              <th className={`${TH} w-[130px]`}>Số quyết định</th><th className={TH}>Nội dung</th><th className={`${TH} w-[190px]`}>Lương</th><th className={`${TH} w-[150px]`}>Hiệu lực</th><th className="py-3 px-4 w-[60px]" />
            </tr></thead>
            <tbody>
              <tr className={TR}>
                <td className="p-4 text-sm text-slate-400">Tiếp nhận</td>
                <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">Vào làm · {positionById(s, moves[0]?.fromPositionId || e.positionId)?.name}</div><div className="text-[13px] text-slate-400 mt-0.5">{branchById(s, moves[0]?.fromBranchId || e.branchId)?.name}</div></td>
                <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{firstOfficial ? fmtMoney(firstOfficial) : '--'}</td>
                <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{fmtD(e.hireDate)}</td>
                <td />
              </tr>
              {moves.map(m => {
                const changed = m.toPositionId !== m.fromPositionId || m.toBranchId !== m.fromBranchId;
                const pct = m.salary && m.prevSalary ? Math.round(((m.salary - m.prevSalary) / m.prevSalary) * 1000) / 10 : 0;
                return (
                  <tr key={m.id} className={TR}>
                    <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{m.decisionNo}</div><div className="text-[12px] text-slate-400">{m.by}</div></td>
                    <td className="p-4">
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-200"><span className="text-[#3b8e9d]">{MOVE_TYPE[m.type].tag}</span>{changed ? <> · {positionById(s, m.fromPositionId)?.name}{m.fromBranchId !== m.toBranchId ? ` (${branchById(s, m.fromBranchId)?.short})` : ''} → {positionById(s, m.toPositionId)?.name}{m.fromBranchId !== m.toBranchId ? ` (${branchById(s, m.toBranchId)?.short})` : ''}</> : ''}</div>
                      {m.reason && <div className="text-[13px] text-slate-400 mt-0.5">{m.reason}</div>}
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                      {m.salary ? <>{fmtMoney(m.salary)}{pct ? <div className={`text-[12px] ${pct > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{pct > 0 ? '+' : ''}{pct}% (từ {fmtM(m.prevSalary || 0)})</div> : null}</> : <span className="text-slate-400">Giữ nguyên</span>}
                    </td>
                    <td className="p-4 text-sm">
                      <div className="text-slate-600 dark:text-slate-300">{fmtD(m.date)}{m.until ? ` – ${fmtD(m.until)}` : ''}</div>
                      <div className={`text-[12px] ${m.applied ? 'text-emerald-600' : 'text-sky-600'}`}>{m.applied ? 'Đã hiệu lực' : `Còn ${daysBetween(today, m.date)} ngày`}</div>
                    </td>
                    <td className="p-4 text-center">{canEdit && !m.applied && (
                      <Popconfirm title="Hủy quyết định chưa hiệu lực?" okText="Hủy quyết định" cancelText="Đóng" onConfirm={() => run(hrApi.cancelMove(e.id, m.id, me), 'Đã hủy quyết định')}>
                        <button type="button" className="text-slate-400 hover:text-rose-500"><MoreHorizontal size={18} /></button>
                      </Popconfirm>
                    )}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Sec>

      <Sec title="Lịch làm việc & nghỉ phép" sub={`Năm ${today.slice(0, 4)} · phép năm 12 ngày, cứ đủ 5 năm làm việc được thêm 1 ngày`} links={canEdit && working && ph !== 'upcoming' && ph !== 'ctv' ? [
        { label: 'Ghi nhận nghỉ', onClick: () => setLeaveOpen(true) },
      ] : undefined}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-6 gap-x-6 mb-6">
          <F label="Lịch làm việc" blue icon={<Calendar size={14} className="mr-1.5" />}>{e.schedule}</F>
          <F label="Mã chấm công">{e.timeCode || e.code}</F>
          <F label="Phép năm" hint={leave.seniority ? `gồm +${leave.seniority} ngày thâm niên` : undefined}>{ph === 'ctv' ? null : `${leave.entitled} ngày${leave.carried ? ` + ${leave.carried} năm trước` : ''}`}</F>
          <F label="Đã nghỉ">{ph === 'ctv' ? null : `${leave.used} ngày`}</F>
          <F label="Còn lại" warn={leave.remain < 0}>{ph === 'ctv' ? null : <span className={leave.remain >= 6 ? 'text-amber-600' : leave.remain > 0 ? 'text-emerald-600' : ''}>{leave.remain} ngày</span>}</F>
        </div>
        {e.leftDate && leave.remain > 0 && <div className="mb-4 text-[13px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Nghỉ việc từ {fmtD(e.leftDate)}: còn {leave.remain} ngày phép chưa nghỉ — thanh toán tiền lương cho những ngày này khi quyết toán.</div>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left border-collapse">
            <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
              <th className={`${TH} w-[190px]`}>Loại nghỉ</th><th className={`${TH} w-[200px]`}>Thời gian</th><th className={`${TH} w-[90px]`}>Số ngày</th><th className={TH}>Lý do</th><th className="py-3 px-4 w-[60px]" />
            </tr></thead>
            <tbody>
              {leaves.length === 0 && <EmptyRow cols={5} text="Chưa có ngày nghỉ trong năm" />}
              {leaves.map(r => (
                <tr key={r.id} className={TR}>
                  <td className="p-3 px-4"><Badge className={LEAVE_KIND[r.kind].badge}>{LEAVE_KIND[r.kind].label}</Badge></td>
                  <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">{fmtD(r.from)}{r.to !== r.from ? ` – ${fmtD(r.to)}` : ''}{r.from <= today && today <= r.to && <span className="ml-2 text-[11px] text-sky-600">đang nghỉ</span>}</td>
                  <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">{r.days}</td>
                  <td className="p-3 px-4 text-sm text-slate-500">{r.reason || '--'}<div className="text-[11px] text-slate-400">Ghi nhận: {r.by}</div></td>
                  <td className="p-3 px-4 text-center">{canEdit && r.from >= today && (
                    <Popconfirm title="Hủy đợt nghỉ chưa diễn ra?" okText="Hủy" cancelText="Đóng" onConfirm={() => { hrApi.removeLeave(e.id, r.id, me); message.success('Đã hủy'); }}>
                      <button type="button" className="text-slate-400 hover:text-rose-500"><MoreHorizontal size={18} /></button>
                    </Popconfirm>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>

      <Sec title="Thuế và bảo hiểm" sub="Thông tin về thuế, bảo hiểm và các chính sách theo kèm" links={canEdit ? [
        (ins === 'missing' || ins === 'late') && { label: 'Đã báo tăng BHXH', onClick: () => run(hrApi.markInsurance(e.id, 'increase', today, me), 'Đã ghi nhận báo tăng') },
        ins === 'decrease_pending' && { label: 'Đã báo giảm BHXH', onClick: () => run(hrApi.markInsurance(e.id, 'decrease', today, me), 'Đã ghi nhận báo giảm') },
        { label: 'Sửa thông tin pháp lý', onClick: () => profile('Sửa thông tin thuế & bảo hiểm', [
          { key: 'taxCode', label: 'Mã số thuế cá nhân' }, { key: 'insuranceNo', label: 'Số sổ BHXH' }, { key: 'insurancePlace', label: 'Nơi đăng ký BHXH', span: true },
        ], 'thông tin thuế & bảo hiểm') },
        { label: 'Thêm người phụ thuộc', onClick: () => listEdit<Dependent>('Đăng ký người phụ thuộc', 'dependentList', DEP_FIELDS, { relation: 'Con', from: today.slice(0, 7) }) },
      ] : undefined}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6">
          <F label="Mã số thuế" warn={!e.taxCode && working} hint={!e.taxCode && working ? 'Cần MST để quyết toán thuế TNCN' : undefined}>{e.taxCode || (working ? 'Chưa có' : null)}</F>
          <F label="Giảm trừ gia cảnh">Bản thân{deps.length ? ` + ${deps.length} người phụ thuộc` : ', không có người phụ thuộc'}</F>
          <F label="Chính sách thuế" blue>{ph === 'ctv' ? 'Khấu trừ 10% (HĐ dịch vụ)' : ph === 'probation' ? 'Lũy tiến (thử việc)' : 'Biểu thuế lũy tiến từng phần'}</F>

          <F label="Số sổ BHXH">{e.insuranceNo}</F>
          <F label="Nơi đăng ký BHXH">{e.insurancePlace}</F>
          <F label="Tình trạng BHXH" hint={(ins === 'missing' || ins === 'late') ? `Hạn đăng ký: ${fmtD(addDays(official || today, INS_DEADLINE_DAYS))}` : ins === 'na' ? 'Thử việc / CTV / chưa ký HĐLĐ — chưa thuộc diện bắt buộc' : undefined}>
            <Badge className={INS_STATE[ins].badge}>{INS_STATE[ins].label}</Badge>
          </F>
          <F label="Ngày báo tăng">{e.ins.increasedAt ? fmtD(e.ins.increasedAt) : null}</F>
          <F label="Ngày báo giảm">{e.ins.decreasedAt ? fmtD(e.ins.decreasedAt) : null}</F>
          <F label="Chính sách bảo hiểm" blue>{ins === 'na' ? '—' : 'Đóng trên lương HĐLĐ'}</F>
        </div>
        {(e.dependentList || []).length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <div className="text-[12px] text-slate-400 mb-1">Người phụ thuộc đăng ký giảm trừ</div>
            <table className="w-full min-w-[640px] text-left border-collapse">
              <thead><tr className="border-b border-dashed border-slate-200 dark:border-slate-700">
                <th className={TH}>Họ tên</th><th className={`${TH} w-[130px]`}>Quan hệ</th><th className={`${TH} w-[120px]`}>Ngày sinh</th><th className={`${TH} w-[130px]`}>MST</th><th className={`${TH} w-[150px]`}>Giảm trừ</th><th className="py-3 px-4 w-[100px]" />
              </tr></thead>
              <tbody>
                {(e.dependentList || []).map(d => {
                  const age = d.dob ? dayjs(today).diff(dayjs(d.dob), 'year') : null;
                  return (
                    <tr key={d.id} className={TR}>
                      <td className="p-3 px-4 text-sm font-medium text-slate-700 dark:text-slate-200">{d.name}</td>
                      <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">{d.relation}</td>
                      <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">{d.dob ? fmtD(d.dob) : '--'}{d.relation === 'Con' && age !== null && age >= 18 && <div className="text-[11px] text-amber-600">Đủ 18 tuổi — chỉ giảm trừ nếu còn đi học</div>}</td>
                      <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">{d.taxCode || <span className="text-amber-600 text-[12px]">Chưa có MST</span>}</td>
                      <td className="p-3 px-4 text-sm text-slate-600 dark:text-slate-300">Từ {dayjs(`${d.from}-01`).format('MM/YYYY')}{d.to ? ` đến ${dayjs(`${d.to}-01`).format('MM/YYYY')}` : ''}</td>
                      <td className="p-3 px-4">{canEdit && <RowActions items={[
                        { key: 'edit', label: 'Sửa', onClick: () => listEdit<Dependent>('Sửa người phụ thuộc', 'dependentList', DEP_FIELDS, d) },
                        { key: 'stop', label: 'Thôi giảm trừ từ tháng này', onClick: () => run(hrApi.saveItem(e.id, 'dependentList', { ...d, to: today.slice(0, 7) }, me), 'Đã cập nhật') },
                        { key: 'del', label: 'Xóa', danger: true, onClick: () => Modal.confirm({ title: 'Xóa người phụ thuộc?', okText: 'Xóa', cancelText: 'Đóng', onOk: () => hrApi.removeItem(e.id, 'dependentList', d.id, me) }) },
                      ]} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <button type="button" onClick={() => setShowInsLog(!showInsLog)} className="w-full mt-6 pt-4 border-t border-dashed border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 font-medium hover:text-slate-900 dark:hover:text-white flex items-center transition-colors">
          {showInsLog ? <ChevronDown size={14} className="mr-2" /> : <ChevronRight size={14} className="mr-2" />} Lịch sử tham gia các chính sách thuế & bảo hiểm trong công ty ({insLog.length + (e.ins.increasedAt ? 1 : 0)})
        </button>
        {showInsLog && (
          <div className="mt-3 space-y-1.5 text-sm pl-6">
            {e.ins.increasedAt && <div><span className="text-slate-400 text-xs mr-2">{fmtD(e.ins.increasedAt)}</span>Báo tăng BHXH lần đầu</div>}
            {insLog.map((l, i) => <div key={i}><span className="text-slate-400 text-xs mr-2">{fmtDateTime(l.at)}</span>{l.text}</div>)}
          </div>
        )}
      </Sec>

      <Sec title="Thông tin học vấn" sub="Chi tiết lịch sử học vấn" links={canEdit ? [{ label: 'Thêm trường học', onClick: () => listEdit<School>('Thêm học vấn', 'schools', SCHOOL_FIELDS, {}) }] : undefined}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left border-collapse">
            <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
              <th className={`${TH} w-[50px] text-center`}>#</th><th className={TH}>Trường học</th><th className={`${TH} w-[160px]`}>Khoảng thời gian</th>
              <th className={`${TH} w-[220px]`}>Bằng cấp</th><th className={`${TH} w-[100px] text-center`}>Cao nhất</th><th className="py-3 px-4 w-[100px]" />
            </tr></thead>
            <tbody>
              {(e.schools || []).length === 0 && <EmptyRow cols={6} text="Chưa có thông tin học vấn" />}
              {(e.schools || []).map((sc, i) => (
                <tr key={sc.id} className={TR}>
                  <td className="p-4 text-slate-400 text-center text-sm">{i + 1}</td>
                  <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{sc.school}</div><div className="text-[13px] text-slate-500 mt-1">{sc.place}</div></td>
                  <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{[sc.from, sc.to].filter(Boolean).join(' – ') || '--'}</td>
                  <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{sc.degree}{sc.major ? <div className="text-[13px] text-slate-500">{sc.major}</div> : null}</td>
                  <td className="p-4 text-center">{sc.highest && <Check size={20} className="text-emerald-500 mx-auto" />}</td>
                  <td className="p-4">{canEdit && <RowActions items={[
                    { key: 'edit', label: 'Sửa', onClick: () => listEdit<School>('Sửa học vấn', 'schools', SCHOOL_FIELDS, sc) },
                    { key: 'del', label: 'Xóa', danger: true, onClick: () => Modal.confirm({ title: 'Xóa dòng học vấn?', okText: 'Xóa', cancelText: 'Đóng', onOk: () => hrApi.removeItem(e.id, 'schools', sc.id, me) }) },
                  ]} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>

      <Sec title="Lịch sử làm việc" sub="Kinh nghiệm trước khi vào công ty" links={canEdit ? [{ label: 'Thêm kinh nghiệm', onClick: () => listEdit<WorkItem>('Thêm kinh nghiệm làm việc', 'workHistory', WORK_FIELDS, {}) }] : undefined}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left border-collapse">
            <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
              <th className={`${TH} w-[50px] text-center`}>#</th><th className={TH}>Công ty</th><th className={TH}>Vị trí</th><th className={`${TH} w-[160px]`}>Khoảng thời gian</th><th className="py-3 px-4 w-[100px]" />
            </tr></thead>
            <tbody>
              {(e.workHistory || []).length === 0 && <EmptyRow cols={5} text="Chưa có kinh nghiệm trước đây" />}
              {(e.workHistory || []).map((w, i) => (
                <tr key={w.id} className={TR}>
                  <td className="p-4 text-slate-400 text-center text-sm">{i + 1}</td>
                  <td className="p-4 text-sm font-medium text-slate-700 dark:text-slate-200">{w.company}</td>
                  <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{w.position}</div>{w.note && <div className="text-[13px] text-slate-500 mt-1">{w.note}</div>}</td>
                  <td className="p-4 text-sm font-medium text-slate-700 dark:text-slate-200">{[w.from, w.to].filter(Boolean).join(' – ') || '--'}</td>
                  <td className="p-4">{canEdit && <RowActions items={[
                    { key: 'edit', label: 'Sửa', onClick: () => listEdit<WorkItem>('Sửa kinh nghiệm làm việc', 'workHistory', WORK_FIELDS, w) },
                    { key: 'del', label: 'Xóa', danger: true, onClick: () => Modal.confirm({ title: 'Xóa dòng kinh nghiệm?', okText: 'Xóa', cancelText: 'Đóng', onOk: () => hrApi.removeItem(e.id, 'workHistory', w.id, me) }) },
                  ]} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>
    </div>
  );

  /* ============================== Tab: Thông tin bổ sung ============================== */
  const renderExtra = () => (
    <div className="space-y-10 pb-10">
      <TabHead title="Thông tin cá nhân bổ sung" sub="Địa chỉ, giấy tờ tùy thân, liên hệ khác và người liên hệ khẩn cấp" links={canEdit ? [{
        label: 'Sửa các thông tin cá nhân bổ sung', onClick: () => profile('Sửa thông tin bổ sung', [
          { key: 'address', label: 'Địa chỉ hiện tại', span: true }, { key: 'permanentAddress', label: 'Hộ khẩu thường trú', span: true },
          { key: 'birthPlace', label: 'Nơi sinh' }, { key: 'nationality', label: 'Quốc tịch' },
          { key: 'cccd', label: 'Số CCCD' }, { key: 'cccdDate', label: 'Ngày cấp', type: 'date' }, { key: 'cccdPlace', label: 'Nơi cấp', span: true },
          { key: 'otherEmail', label: 'Email khác' }, { key: 'otherPhone', label: 'Số điện thoại khác' }, { key: 'nickname', label: 'Nickname' }, { key: 'facebook', label: 'Facebook / Zalo' }, { key: 'note', label: 'Ghi chú hồ sơ', type: 'textarea' },
          { key: 'emergency.name', label: 'Người liên hệ khẩn cấp' }, { key: 'emergency.relation', label: 'Quan hệ' }, { key: 'emergency.phone', label: 'SĐT khẩn cấp' },
        ], 'thông tin bổ sung', v => {
          const { 'emergency.name': en, 'emergency.relation': er, 'emergency.phone': ep, ...rest } = v;
          return { ...(rest as Partial<Employee>), emergency: en || ep ? { name: String(en || ''), relation: String(er || ''), phone: String(ep || '') } : undefined };
        }),
      }] : undefined} />
      <div className="border-t border-slate-200 dark:border-slate-700 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 mb-8">
          <F label="Địa chỉ hiện tại">{e.address}</F>
          <F label="Nơi sinh">{e.birthPlace}</F>
          <F label="Hộ khẩu thường trú">{e.permanentAddress}</F>
          <F label="Quốc tịch">{e.nationality}</F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 mb-8">
          <F label="CMT/Căn cước">{e.cccd}</F>
          <F label="Ngày cấp">{e.cccdDate ? fmtD(e.cccdDate) : null}</F>
          <F label="Nơi cấp">{e.cccdPlace}</F>
        </div>
        <div className="border-t border-dashed border-slate-200 dark:border-slate-700 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 mb-8">
          <F label="Email khác">{e.otherEmail}</F>
          <F label="Số điện thoại khác">{e.otherPhone}</F>
          <F label="Nickname">{e.nickname}</F>
          <F label="Facebook / Zalo" blue>{e.facebook}</F>
          <F label="Ghi chú hồ sơ">{e.note}</F>
        </div>
        <div className="border-t border-dashed border-slate-200 dark:border-slate-700 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6">
          <F label="Người liên hệ khẩn cấp">{e.emergency?.name}</F>
          <F label="Quan hệ">{e.emergency?.relation}</F>
          <F label="Số điện thoại khẩn cấp">{e.emergency?.phone}</F>
        </div>
      </div>
    </div>
  );

  /* ============================== Tab: Hợp đồng & Lương ============================== */
  const renderContracts = () => {
    const list = [...contracts].reverse();
    const nFixed = fixedCount(s, e.id);
    const renderContractRow = (c: Contract) => {
      const idx = contracts.indexOf(c);
      const prev = contracts[idx - 1];
      const ev = careerEvent(c, prev);
      const st = contractState(s, c, today);
      const prevPay = prev ? (prev.kind === 'probation' ? prev.officialSalary || prev.salary : prev.salary) : 0;
      const diff = prevPay && c.kind !== 'probation' ? Math.round(((c.salary - prevPay) / prevPay) * 1000) / 10 : 0;
      const ring = st === 'ended' ? 'border-slate-300' : CSTATE[st].level === 'crit' ? 'border-rose-500' : CSTATE[st].level ? 'border-amber-500' : 'border-emerald-500';
      const since = daysBetween(c.start, today);
      const actions = [
        !c.signedAt && st !== 'ended' && { key: 'sign', label: 'Xác nhận đã ký', onClick: () => run(hrApi.signContract(c.id, today, me), `Đã ký ${c.code}`) },
        c.kind === 'probation' && !c.evaluation && c.start <= today && { key: 'eval', label: 'Đánh giá thử việc', onClick: () => setEvalFor(c.id) },
        ['expiring', 'expired', 'auto_indefinite'].includes(st) && { key: 'renew', label: 'Gia hạn / ký tiếp', onClick: () => setContractInit({}) },
        !c.signedAt && { key: 'cancel', label: 'Hủy HĐ chưa ký', danger: true, onClick: () => Modal.confirm({ title: `Hủy ${c.code}?`, okText: 'Hủy HĐ', cancelText: 'Đóng', onOk: () => run(hrApi.cancelContract(c.id, me), 'Đã hủy') }) },
      ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[];
      return (
        <tr key={c.id} className={TR}>
          <td className="p-4 text-center"><div className={`w-3.5 h-3.5 rounded-full border-2 mx-auto ${ring}`} /></td>
          <td className="p-4">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200"><span className={ev.tag === 'TUYỂN DỤNG' ? 'text-amber-600' : ''}>{ev.tag}</span> &rarr; {pos?.name}</div>
            <div className="text-[13px] text-slate-500 dark:text-slate-400 mt-1">{c.code} · {CONTRACT_KIND[c.kind].label} · {fmtD(c.start)} – {c.end ? fmtD(c.end) : 'không thời hạn'}</div>
            {c.evaluation && <div className={`text-[12px] mt-0.5 ${c.evaluation.result === 'pass' ? 'text-emerald-600' : 'text-rose-600'}`}>Thử việc {c.evaluation.result === 'pass' ? 'đạt' : 'không đạt'}{c.evaluation.score ? ` · ${c.evaluation.score}/10` : ''}{c.evaluation.note ? ` · ${c.evaluation.note}` : ''}</div>}
          </td>
          <td className="p-4">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(c.salary)}</div>
            <div className="text-[13px] mt-1">
              {c.kind === 'probation' && c.officialSalary ? <span className="text-slate-500">chính thức {fmtMoney(c.officialSalary)}</span>
                : diff ? <span className={diff > 0 ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'}>{diff > 0 ? '+' : ''}{diff}% so với trước</span> : <span className="text-slate-400">giữ nguyên</span>}
            </div>
          </td>
          <td className="p-4">
            <div className={`text-sm font-semibold ${st === 'ended' ? 'text-slate-400' : CSTATE[st].level === 'crit' ? 'text-rose-600' : CSTATE[st].level ? 'text-amber-600' : 'text-emerald-500'}`}>{CSTATE[st].label}</div>
            <div className="text-[13px] mt-1">{c.signedAt ? <span className="text-slate-500">Ký {fmtD(c.signedAt)} · {since >= 0 ? `từ ${since} ngày trước` : `hiệu lực sau ${-since} ngày`}</span> : <span className="text-rose-600 font-medium">Chưa ký</span>}</div>
          </td>
          <td className="p-4 text-center">
            {canEdit && actions.length > 0 ? (
              <Dropdown trigger={['click']} menu={{ items: actions.map(a => ({ key: a.key, label: a.label, danger: a.danger })), onClick: ({ key }) => actions.find(a => a.key === key)?.onClick() }}>
                <button type="button" className="text-slate-400 hover:text-slate-900 dark:hover:text-white"><MoreHorizontal size={20} /></button>
              </Dropdown>
            ) : null}
          </td>
        </tr>
      );
    };
    return (
      <div className="space-y-10 pb-10">
        <TabHead title="Hợp đồng & Lương" sub="Các thông tin về hợp đồng, lịch sử lương và tài khoản ngân hàng" />
        <Sec title="Lịch sử phát triển sự nghiệp" sub="Thử việc, ký chính thức, tái ký hợp đồng và các quyết định bổ nhiệm, điều chuyển, điều chỉnh lương" links={canEdit && working ? [
          { label: 'Lập hợp đồng / gia hạn', onClick: () => setContractInit({}) },
          { label: 'Điều chỉnh lương', onClick: () => setMove('salary') },
        ] : undefined}>
          <div className="flex flex-col lg:flex-row gap-10 mb-10">
            <div className="flex-1 min-w-0">
              <h5 className="text-center text-base font-medium text-slate-700 dark:text-slate-200 mb-4">Mức lương theo thời gian</h5>
              {series.length > 1 ? (
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtM(v)} width={56} allowDecimals={false} domain={[0, (max: number) => Math.ceil((max * 1.2) / 5e6) * 5e6]} />
                      <RTooltip formatter={(v: number) => fmtMoney(v)} labelFormatter={l => `Tháng ${l}`} />
                      <Line type="stepAfter" dataKey="salary" name="Lương" stroke="#3b82f6" strokeWidth={3} dot={{ r: 5, fill: '#3b82f6' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="h-[120px] flex items-center justify-center text-sm text-slate-400">Chưa đủ dữ liệu lương</div>}
              <div className="text-[12px] text-slate-500 dark:text-slate-400 text-center mt-4">Lưu ý: đây là mức lương trên hợp đồng. <b className="text-slate-700 dark:text-slate-200">Thu nhập thực tế</b> có thể khác (hoa hồng, phụ cấp, tăng ca).</div>
            </div>
            <div className="lg:w-[320px] flex-shrink-0 lg:border-l border-slate-200 dark:border-slate-700 lg:pl-10">
              <div className="pb-5 border-b border-slate-200 dark:border-slate-700 mb-5">
                <div className="text-[12px] text-slate-400 dark:text-slate-500 mb-1">Lương khi tuyển (chính thức thỏa thuận)</div>
                <div className="text-base font-semibold text-emerald-600 mt-1">{firstOfficial ? fmtMoney(firstOfficial) : '--'}</div>
              </div>
              <div className="pb-5 border-b border-slate-200 dark:border-slate-700 mb-5 flex justify-between items-start">
                <div>
                  <div className="text-[12px] text-slate-400 dark:text-slate-500 mb-1">Lương hiện tại{cur?.kind === 'probation' ? ' (thử việc)' : ''}</div>
                  <div className="text-base font-semibold text-emerald-600 mt-1">{nowSalary ? fmtMoney(nowSalary) : '--'}</div>
                </div>
                {firstOfficial > 0 && nowSalary > 0 && cur?.kind !== 'probation' && nowSalary !== firstOfficial && (
                  <div className="text-right">
                    <div className={`text-sm font-semibold flex items-center justify-end ${nowSalary > firstOfficial ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {nowSalary > firstOfficial ? <ArrowUp size={16} className="mr-1" /> : <ArrowDown size={16} className="mr-1" />}{fmtMoney(Math.abs(nowSalary - firstOfficial))}
                    </div>
                    <div className="text-[13px] text-slate-500 mt-0.5">({Math.round(((nowSalary - firstOfficial) / firstOfficial) * 1000) / 10}%)</div>
                  </div>
                )}
              </div>
              <div className="flex justify-between pb-5 border-b border-slate-200 dark:border-slate-700 mb-5">
                <div className="text-[12px] text-slate-400 dark:text-slate-500">Tăng bình quân / năm</div>
                <div className="text-sm font-semibold text-emerald-600 flex items-center">{yearly !== null ? <><ArrowUp size={16} className="mr-1" />{yearly}%</> : <span className="text-slate-400 font-normal">--</span>}</div>
              </div>
              <div className="flex justify-between pb-5 border-b border-slate-200 dark:border-slate-700 mb-5">
                <div className="text-[12px] text-slate-400 dark:text-slate-500">Ngày làm việc</div>
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{tenureText(e.hireDate, endDate)}</div>
              </div>
              <div className="flex justify-between pb-5 border-b border-slate-200 dark:border-slate-700 mb-5">
                <div className="text-[12px] text-slate-400 dark:text-slate-500">HĐ xác định thời hạn đã ký</div>
                <Tooltip title={nFixed >= FIXED_MAX_TIMES ? 'Lần ký tiếp theo bắt buộc HĐ không xác định thời hạn' : undefined}>
                  <div className={`text-sm font-semibold ${nFixed >= FIXED_MAX_TIMES ? 'text-amber-600' : 'text-slate-700 dark:text-slate-200'}`}>{nFixed}/{FIXED_MAX_TIMES} lần</div>
                </Tooltip>
              </div>
              <div className="flex justify-between">
                <div className="text-[12px] text-slate-400 dark:text-slate-500">Phát triển</div>
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{contracts.length + moves.filter(m => m.applied).length} bản ghi</div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left border-collapse">
              <thead><tr className="border-b border-slate-200 dark:border-slate-700">
                <th className={`${TH} w-[50px] text-center`}>#</th><th className={TH}>Vị trí / hợp đồng</th><th className={`${TH} w-[200px]`}>Lương</th><th className={`${TH} w-[220px]`}>Trạng thái</th><th className="py-3 px-4 w-[60px]" />
              </tr></thead>
              <tbody>
                {list.length === 0 && <EmptyRow cols={5} text="Chưa có hợp đồng" />}
                {moves.filter(m => m.applied && list.length > 0).map(m => {
                  const pct = m.salary && m.prevSalary ? Math.round(((m.salary - m.prevSalary) / m.prevSalary) * 1000) / 10 : 0;
                  return { date: m.date, node: (
                    <tr key={m.id} className={TR}>
                      <td className="p-4 text-center"><div className="w-3.5 h-3.5 rounded-full border-2 mx-auto border-sky-400" /></td>
                      <td className="p-4">
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200"><span className="text-sky-600">{MOVE_TYPE[m.type].tag}</span> &rarr; {positionById(s, m.toPositionId)?.name}{m.toBranchId !== m.fromBranchId ? ` · ${branchById(s, m.toBranchId)?.short}` : ''}</div>
                        <div className="text-[13px] text-slate-500 dark:text-slate-400 mt-1">Quyết định {m.decisionNo} · hiệu lực {fmtD(m.date)}{m.reason ? ` · ${m.reason}` : ''}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{m.salary ? fmtMoney(m.salary) : '--'}</div>
                        <div className="text-[13px] mt-1">{pct ? <span className={pct > 0 ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'}>{pct > 0 ? '+' : ''}{pct}% so với trước</span> : <span className="text-slate-400">giữ nguyên lương</span>}</div>
                      </td>
                      <td className="p-4"><div className="text-sm font-semibold text-sky-600">Theo quyết định</div><div className="text-[13px] mt-1 text-slate-500">{m.by}</div></td>
                      <td />
                    </tr>
                  ) };
                }).concat(list.map(c => ({ date: c.start, node: renderContractRow(c) }))).sort((a, b) => b.date.localeCompare(a.date)).map(x => x.node)}
              </tbody>
            </table>
          </div>
        </Sec>


        <Sec title="Tài khoản ngân hàng" sub="Tài khoản nhận lương" links={canEdit ? [{
          label: 'Sửa thông tin tài khoản ngân hàng', onClick: () => profile('Sửa tài khoản ngân hàng', [
            { key: 'bank.account', label: 'Số tài khoản' }, { key: 'bank.bank', label: 'Ngân hàng', type: 'select', options: ['Vietcombank', 'Techcombank', 'ACB', 'MB Bank', 'VPBank', 'BIDV', 'VietinBank', 'Sacombank', 'TPBank'].map(x => ({ value: x, label: x })) },
            { key: 'bank.branch', label: 'Chi nhánh ngân hàng', span: true },
          ], 'tài khoản ngân hàng', v => ({ bank: v['bank.account'] ? { account: String(v['bank.account']).replace(/\s/g, ''), bank: String(v['bank.bank'] || ''), branch: (v['bank.branch'] as string) || undefined } : undefined })),
        }] : undefined}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
            <F label="Số tài khoản Ngân hàng" warn={!e.bank && working}>{e.bank?.account || (working ? 'Chưa có — không chi lương chuyển khoản được' : null)}</F>
            <F label="Tên chủ tài khoản"><span className="uppercase">{normText(e.name).toUpperCase()}</span></F>
            <F label="Tên Ngân hàng">{e.bank?.bank}</F>
            <F label="Chi nhánh Ngân hàng">{e.bank?.branch}</F>
          </div>
        </Sec>
      </div>
    );
  };

  /* ============================== Tab: Giấy phép & hồ sơ ============================== */
  const renderDocs = () => {
    const keys = requiredDocs(s, e);
    const got = keys.filter(k => e.docs[k]).length;
    return (
      <div className="space-y-10 pb-10">
        <TabHead title="Giấy phép & hồ sơ" sub="Giấy phép hành nghề, chứng chỉ chuyên môn, giấy tờ nhận việc và khám sức khỏe" />
        <Sec title="Giấy phép hành nghề / chứng chỉ" sub={pos?.license ? `Vị trí ${pos.name} bắt buộc có ${LICENSE_KIND[pos.license].toLowerCase()} còn hiệu lực` : 'Vị trí không yêu cầu giấy phép hành nghề'}
          links={canEdit ? [{ label: 'Thêm giấy phép', onClick: () => setLic({ kind: pos?.license || 'cert' }) }] : undefined} first>
          {pos?.license && (
            <div className={`mb-4 rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${licState.state === 'ok' ? 'bg-emerald-50 text-emerald-700' : licState.state === 'expiring' ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700'}`}>
              {licState.state === 'ok' ? <BadgeCheck size={16} /> : <ShieldAlert size={16} />}
              {licState.state === 'ok' ? 'Giấy phép hợp lệ' : licState.state === 'expiring' ? `Giấy phép hết hạn sau ${licState.days} ngày — làm hồ sơ gia hạn` : licState.state === 'expired' ? 'Giấy phép đã hết hạn — không được thực hiện thủ thuật' : 'Chưa có giấy phép — không được thực hiện thủ thuật'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left border-collapse">
              <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
                <th className={`${TH} w-[50px] text-center`}>#</th><th className={TH}>Loại / số</th><th className={`${TH} w-[130px]`}>Ngày cấp</th><th className={`${TH} w-[160px]`}>Hết hạn</th><th className={TH}>Phạm vi</th><th className="py-3 px-4 w-[100px]" />
              </tr></thead>
              <tbody>
                {e.licenses.length === 0 && <EmptyRow cols={6} text="Chưa có giấy phép / chứng chỉ" />}
                {e.licenses.map((l, i) => {
                  const d = l.expiresAt ? daysBetween(today, l.expiresAt) : null;
                  return (
                    <tr key={l.id} className={TR}>
                      <td className="p-4 text-slate-400 text-center text-sm">{i + 1}</td>
                      <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{LICENSE_KIND[l.kind]}</div><div className="text-[13px] text-slate-500 mt-1">Số {l.number}</div></td>
                      <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{fmtD(l.issuedAt)}</td>
                      <td className={`p-4 text-sm font-medium ${d === null ? 'text-slate-500' : d < 0 ? 'text-rose-600' : d <= 90 ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300'}`}>{l.expiresAt ? fmtD(l.expiresAt) : 'Không thời hạn'}{d !== null && <div className="text-[12px]">{d < 0 ? `quá hạn ${-d} ngày` : `còn ${d} ngày`}</div>}</td>
                      <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{l.scope || '--'}</td>
                      <td className="p-4">{canEdit && <RowActions items={[
                        { key: 'edit', label: 'Sửa', onClick: () => setLic({ ...l }) },
                        { key: 'del', label: 'Xóa', danger: true, onClick: () => Modal.confirm({ title: 'Xóa giấy phép?', okText: 'Xóa', cancelText: 'Đóng', onOk: () => hrApi.removeLicense(e.id, l.id, me) }) },
                      ]} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pos?.license === 'gphn' && working && (
            <div className="mt-5 flex items-center gap-2 text-sm">
              <Switch size="small" disabled={!canEdit} checked={!!e.medicalLead} onChange={v => run(hrApi.setMedicalLead(e.id, v, me), v ? 'Đã chỉ định phụ trách chuyên môn' : 'Đã bỏ chỉ định')} />
              Người phụ trách chuyên môn của {branch?.name}
            </div>
          )}
        </Sec>
        <Sec title="Giấy tờ nhận việc" sub={`Đã nhận ${got}/${keys.length} giấy tờ bắt buộc`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {keys.map((k: DocKey) => (
              <label key={k} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${e.docs[k] ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                <Checkbox disabled={!canEdit} checked={!!e.docs[k]} onChange={x => hrApi.setDoc(e.id, k, x.target.checked, me)} />
                <span className={e.docs[k] ? 'text-slate-800 dark:text-slate-100' : 'text-amber-800'}>{DOC_KEYS[k]}</span>
              </label>
            ))}
          </div>
        </Sec>
        <Sec title="Khám sức khỏe định kỳ" sub="Tối thiểu mỗi năm một lần">
          <div className="flex flex-wrap items-center gap-6">
            <F label="Lần khám gần nhất">{e.healthCheck ? fmtD(e.healthCheck) : null}</F>
            <F label="Tình trạng">
              <Badge className={health.state === 'ok' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : health.state === 'due' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200'}>
                {health.state === 'ok' ? `Còn hạn ${health.days} ngày` : health.state === 'due' ? `Sắp đến hạn (${health.days} ngày)` : health.state === 'overdue' ? `Quá hạn ${-health.days!} ngày` : 'Chưa có'}
              </Badge>
            </F>
            {canEdit && <Button size="small" onClick={() => { hrApi.setHealthCheck(e.id, today, me); message.success('Đã cập nhật ngày khám'); }}>Đã khám hôm nay</Button>}
          </div>
        </Sec>
      </div>
    );
  };

  /* ============================== Tab: Tài sản cấp phát ============================== */
  const renderAssets = () => {
    const list = [...(e.assets || [])].sort((a, b) => Number(!!a.returnedAt) - Number(!!b.returnedAt) || b.issuedAt.localeCompare(a.issuedAt));
    const holding = pending.reduce((a, x) => a + (x.value || 0), 0);
    return (
      <div className="space-y-10 pb-10">
        <TabHead title="Tài sản cấp phát" sub="Trang thiết bị, đồng phục, dụng cụ công ty đã cấp phát cho nhân sự"
          links={canEdit && working ? [
            { label: <span className="flex items-center">Cấp phát mới <ChevronDown size={14} className="ml-1" /></span>, onClick: () => listEdit<AssetItem>('Cấp phát tài sản', 'assets', ASSET_FIELDS, { kind: 'device', issuedAt: today, condition: 'Tốt' }) },
            pending.length > 0 && { label: 'Thu hồi tất cả', onClick: () => Modal.confirm({ title: `Thu hồi ${pending.length} tài sản?`, content: pending.map(a => a.name).join(', '), okText: 'Thu hồi', cancelText: 'Đóng', onOk: () => run(hrApi.returnAssets(e.id, pending.map(a => a.id), today, me), 'Đã thu hồi') }) },
          ] : undefined} />
        {e.leftDate && pending.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">Nhân sự {e.leftDate < today ? 'đã nghỉ' : `nghỉ từ ${fmtD(e.leftDate)}`} nhưng còn {pending.length} tài sản chưa thu hồi (trị giá {fmtMoney(holding)}) — thủ tục nghỉ việc chưa hoàn tất được.</div>
        )}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-8">
          <div className="text-sm text-slate-500 mb-4">Đang giữ <b className="text-slate-700 dark:text-slate-200">{pending.length}</b> tài sản · trị giá <b className="text-slate-700 dark:text-slate-200">{fmtMoney(holding)}</b></div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left border-collapse">
              <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700">
                <th className={`${TH} w-[60px] text-center`}>#</th><th className={TH}>Tài sản / Thiết bị</th><th className={`${TH} w-[140px]`}>Mã tài sản</th><th className={`${TH} w-[120px]`}>Ngày cấp</th><th className={`${TH} w-[170px]`}>Tình trạng</th><th className="py-3 px-4 w-[130px]" />
              </tr></thead>
              <tbody>
                {list.length === 0 && <EmptyRow cols={6} text="Chưa cấp phát tài sản" />}
                {list.map(a => (
                  <tr key={a.id} className={`${TR} ${a.returnedAt ? 'opacity-60' : ''}`}>
                    <td className="p-4 text-center"><div className={`w-10 h-10 rounded-md flex items-center justify-center mx-auto ${ASSET_ICON[a.kind].cls}`}>{ASSET_ICON[a.kind].icon}</div></td>
                    <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{a.name}</div><div className="text-[13px] text-slate-500 mt-1">{ASSET_KIND[a.kind]}{a.value ? ` · ${fmtMoney(a.value)}` : ''}{a.note ? ` · ${a.note}` : ''}</div></td>
                    <td className="p-4 text-sm font-medium text-slate-700 dark:text-slate-200">{a.code}</td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{fmtD(a.issuedAt)}</td>
                    <td className="p-4">
                      {a.returnedAt
                        ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12.5px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">Đã thu hồi {fmtD(a.returnedAt)}</span>
                        : <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12.5px] font-semibold ${a.condition && a.condition !== 'Tốt' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400'}`}>{a.condition || 'Đang sử dụng'}</span>}
                    </td>
                    <td className="p-4">{canEdit && !a.returnedAt && <RowActions items={[
                      { key: 'return', label: 'Thu hồi', onClick: () => Modal.confirm({ title: `Thu hồi ${a.name}?`, okText: 'Thu hồi', cancelText: 'Đóng', onOk: () => run(hrApi.returnAssets(e.id, [a.id], today, me), 'Đã thu hồi') }) },
                      { key: 'edit', label: 'Sửa', onClick: () => listEdit<AssetItem>('Sửa tài sản cấp phát', 'assets', ASSET_FIELDS, a) },
                      { key: 'del', label: 'Xóa (cấp nhầm)', danger: true, onClick: () => Modal.confirm({ title: 'Xóa dòng cấp phát?', okText: 'Xóa', cancelText: 'Đóng', onOk: () => hrApi.removeItem(e.id, 'assets', a.id, me) }) },
                    ]} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ============================== Tab: Vi phạm & khen thưởng ============================== */
  const renderRecords = () => {
    const recs = [...(e.records || [])].sort((a, b) => b.date.localeCompare(a.date));
    const fire = recs.find(r => r.level === 'Sa thải');
    const table = (kind: RecordKind, title: string, sub: string) => {
      const rows = recs.filter(r => r.kind === kind);
      return (
        <Sec title={title} sub={sub} links={canEdit ? [{ label: kind === 'reward' ? 'Thêm khen thưởng' : 'Ghi nhận vi phạm', onClick: () => listEdit<RecordItem>(kind === 'reward' ? 'Thêm khen thưởng' : 'Ghi nhận vi phạm', 'records', kind === 'reward' ? REWARD_FIELDS : VIOLATION_FIELDS, { kind, date: today, by: me }, v => ({ ...v, kind, by: me })) }] : undefined}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[520px]">
              <thead><tr className="border-b border-t border-dashed border-slate-200 dark:border-slate-700 text-left">
                <th className={`${TH} w-[50px] text-center`}>#</th><th className={TH}>Sự vụ</th><th className={`${TH} w-[200px]`}>{kind === 'reward' ? 'Mức thưởng' : 'Hình thức xử lý'}</th><th className={`${TH} w-[140px]`}>Ngày</th><th className="py-3 px-4 w-[60px]" />
              </tr></thead>
              <tbody>
                {rows.length === 0 && <EmptyRow cols={5} text={kind === 'reward' ? 'Chưa có khen thưởng' : 'Không có dữ liệu vi phạm'} />}
                {rows.map((r, i) => (
                  <tr key={r.id} className={TR}>
                    <td className="p-4 text-slate-400 text-center text-sm">{i + 1}</td>
                    <td className="p-4"><div className="text-sm font-medium text-slate-700 dark:text-slate-200">{r.title}</div>{r.note && <div className="text-[13px] text-slate-500 mt-1">{r.note}</div>}<div className="text-[12px] text-slate-400 mt-0.5">Ghi nhận: {r.by}</div></td>
                    <td className="p-4 text-sm">
                      {kind === 'reward' ? <span className="font-semibold text-emerald-600">{r.amount ? fmtMoney(r.amount) : 'Giấy khen'}</span>
                        : <span className={`font-semibold ${r.level === 'Sa thải' || r.level === 'Cách chức' ? 'text-rose-600' : r.level === 'Khiển trách' || r.level?.startsWith('Kéo dài') ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300'}`}>{r.level || '--'}{r.amount ? <div className="text-[12px] font-normal text-slate-500">Bồi thường {fmtMoney(r.amount)}</div> : null}</span>}
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300">{fmtD(r.date)}</td>
                    <td className="p-4 text-center">{canEdit && (
                      <Popconfirm title="Xóa dòng này?" okText="Xóa" cancelText="Đóng" onConfirm={() => hrApi.removeItem(e.id, 'records', r.id, me)}>
                        <button type="button" className="text-slate-400 hover:text-rose-600"><MoreHorizontal size={18} /></button>
                      </Popconfirm>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>
      );
    };
    return (
      <div className="space-y-10 pb-10">
        <TabHead green title="Khen thưởng & vi phạm" sub="Thành tích, khen thưởng và các vi phạm quy định làm việc, chính sách tài sản" />
        {fire && working && !e.leftDate && canEdit && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700 flex flex-wrap items-center gap-3">
            Có quyết định xử lý kỷ luật hình thức <b>sa thải</b> ngày {fmtD(fire.date)} nhưng nhân sự vẫn đang làm việc.
            <Button size="small" danger onClick={() => setOffboard('discipline')}>Lập thủ tục sa thải</Button>
          </div>
        )}
        {table('work', 'Các vi phạm quy định làm việc', 'Đi trễ, sai quy trình chuyên môn, vi phạm nội quy')}
        {table('asset', 'Vi phạm chính sách tài sản', 'Làm mất, hư hỏng tài sản / dụng cụ công ty')}
        {table('reward', 'Khen thưởng', 'Thành tích, danh hiệu, thưởng nóng')}
      </div>
    );
  };

  const renderLog = () => (
    <div className="space-y-6 pb-10">
      <TabHead title="Lịch sử" sub="Mọi thay đổi trên hồ sơ nhân sự" />
      <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-3">
        {[...e.log].reverse().map((l, i) => (
          <div key={i} className="flex gap-3 text-sm">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <div><div className="text-slate-700 dark:text-slate-200">{l.text}</div><div className="text-[12px] text-slate-400">{fmtDateTime(l.at)} · {l.by}</div></div>
          </div>
        ))}
      </div>
    </div>
  );

  const badgeCount: Partial<Record<TabKey, number>> = {
    contracts: acts.filter(a => a.group === 'contract' || a.group === 'probation').length,
    docs: acts.filter(a => a.group === 'license' || a.group === 'docs').length,
    info: acts.filter(a => a.group === 'insurance').length,
    assets: e.leftDate ? pending.length : 0,
  };

  return (
    <Layout>
      <div className="h-auto md:h-[calc(100vh-80px)] min-h-[calc(100vh-80px)] w-full mx-auto bg-slate-50 dark:bg-slate-900 md:rounded-xl md:overflow-hidden shadow-sm border-0 md:border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row">

        {/* CỘT TRÁI: tóm tắt nhân sự */}
        <div className="w-full md:w-[320px] bg-white dark:bg-slate-800 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700 flex flex-col items-center px-6 py-8 shrink-0 md:h-full md:overflow-y-auto custom-scrollbar">
          <button type="button" onClick={() => navigate(-1)} className="self-start mb-8 text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2 hover:text-[#3b8e9d] dark:hover:text-blue-400 transition-colors">
            <ArrowLeft size={16} /> Quay lại danh sách
          </button>

          <div className="relative mb-5">
            <div className={`w-[140px] h-[140px] rounded-full ${avatarColor(e.name)} text-white flex items-center justify-center text-5xl font-bold shadow-md`}>{initials(e.name)}</div>
            <Tooltip title={PHASE[ph].label}>
              <div className={`absolute bottom-2 right-2 w-8 h-8 ${PHASE_DOT[ph]} rounded-full border-[3px] border-white dark:border-slate-800 flex items-center justify-center text-white shadow-sm`}>
                {ph === 'left' ? <LogOut size={13} strokeWidth={3} /> : ph === 'paused' ? <PauseCircle size={14} strokeWidth={3} /> : <Check size={14} strokeWidth={3} />}
              </div>
            </Tooltip>
          </div>

          <h2 className="text-[21px] font-semibold text-center leading-tight mb-1 text-slate-800 dark:text-slate-100">{e.name}</h2>
          <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2 text-center">{pos?.name}</div>
          <div className="flex flex-wrap justify-center gap-1.5 mb-6">
            <Badge className={PHASE[ph].badge}>{PHASE[ph].label}</Badge>
            {e.medicalLead && working && <Badge className="bg-violet-50 text-violet-700 border-violet-200"><ShieldCheck size={11} />PT chuyên môn</Badge>}
            {e.noRehire && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Không tái tuyển</Badge>}
          </div>

          {canEdit && menuItems.length > 0 && (
            <div className="w-full flex gap-2 mb-6">
              <Button className="flex-1" icon={<Pencil size={14} />} onClick={() => navigate(`/personnel-management/add?id=${e.id}`)}>Sửa hồ sơ</Button>
              <Dropdown trigger={['click']} menu={{ items: menuItems.map(m => ({ key: m.key, label: m.label, icon: m.icon, danger: m.danger })), onClick: ({ key }) => onMenu(key) }}>
                <Button type="primary">Thao tác <ChevronDown size={14} /></Button>
              </Dropdown>
            </div>
          )}

          <div className="w-full border-t border-slate-200 dark:border-slate-700 pt-6 flex flex-col gap-4 text-sm text-slate-600 dark:text-slate-300">
            <div className="flex gap-4 items-center"><CalendarCheck className="w-5 shrink-0 text-[#3b8e9d]" /> <b className="font-semibold text-slate-700 dark:text-slate-200">{tenureText(e.hireDate, endDate)} làm việc</b></div>
            <div className="flex gap-4 items-center"><IdCard className="w-5 shrink-0 text-slate-400" /> <span>{e.code}</span></div>
            <div className="flex gap-4 items-center"><Briefcase className="w-5 shrink-0 text-slate-400" /> <span>{pos ? LEVEL[pos.level] : ''} · {dept?.name}</span></div>
            <div className="flex gap-4 items-center"><Building className="w-5 shrink-0 text-slate-400" /> <span>{branch?.name}</span></div>
            {e.email && <div className="flex gap-4 items-center"><Mail className="w-5 shrink-0 text-slate-400" /> <a href={`mailto:${e.email}`} className="break-all text-[#3b8e9d]">{e.email}</a></div>}
            <div className="flex gap-4 items-center"><Phone className="w-5 shrink-0 text-slate-400" /> <a href={`tel:${e.phone}`} className="hover:text-[#3b8e9d]">{e.phone}</a></div>
            {e.leftDate && <div className="flex gap-4 items-center text-rose-600"><LogOut className="w-5 shrink-0" /> <span>{e.leftDate < today ? 'Đã nghỉ' : 'Nghỉ từ'} {fmtD(e.leftDate)}{e.leaveType ? ` · ${LEAVE_TYPE[e.leaveType]}` : ''}</span></div>}
            {e.paused && <div className="flex gap-4 items-center text-violet-600"><PauseCircle className="w-5 shrink-0" /> <span>{e.paused.reason} · {fmtD(e.paused.from)} – {fmtD(e.paused.to)}</span></div>}
            {e.candidateId && <div className="flex gap-4 items-center"><GraduationCap className="w-5 shrink-0 text-slate-400" /> <button type="button" className="text-[#3b8e9d] hover:underline text-left" onClick={() => navigate(candUrl(e.candidateId!))}>Xem quá trình tuyển dụng</button></div>}
          </div>

          {acts.length > 0 && (
            <div className="w-full mt-6 border-t border-slate-200 dark:border-slate-700 pt-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><ClipboardList size={14} /> Cần xử lý ({acts.length})</div>
              <div className="space-y-2.5">
                {acts.map(a => (
                  <button key={a.key} type="button" onClick={() => setTab(a.group === 'license' || a.group === 'docs' ? 'docs' : a.group === 'insurance' ? 'info' : a.group === 'offboarding' ? 'assets' : 'contracts')}
                    className="w-full text-left flex gap-2 items-start text-[13px] hover:bg-slate-50 dark:hover:bg-slate-700/40 rounded-md p-1 -m-1">
                    <span className="mt-1.5"><LevelDot level={a.level} /></span>
                    <span className="text-slate-800 dark:text-slate-200 leading-snug">{a.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(e.records || []).some(r => r.kind === 'reward') && (
            <div className="w-full mt-5 flex items-center gap-2 text-[13px] text-emerald-700"><Award size={15} /> {(e.records || []).filter(r => r.kind === 'reward').length} lần khen thưởng</div>
          )}
          {e.bank && <div className="w-full mt-3 flex items-center gap-2 text-[13px] text-slate-500"><CreditCard size={15} /> {e.bank.bank} · ****{e.bank.account.slice(-4)}</div>}
        </div>

        {/* CỘT PHẢI: các tab */}
        <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-slate-800 md:h-full relative overflow-visible md:overflow-hidden">
          <div className="flex gap-6 md:gap-8 px-4 md:px-8 border-b border-slate-200 dark:border-slate-700 shrink-0 overflow-x-auto no-scrollbar bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm sticky md:top-0 top-[64px] z-20">
            {TABS.map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`py-4 text-sm whitespace-nowrap border-b-2 transition-all flex items-center gap-1.5 ${tab === t.key ? 'border-[#3b8e9d] text-[#3b8e9d] font-semibold' : 'border-transparent text-slate-500 dark:text-slate-400 font-medium hover:text-slate-900 dark:hover:text-white'}`}>
                {t.label}
                {badgeCount[t.key] ? <span className="text-[10px] font-bold px-1.5 rounded-full bg-rose-100 text-rose-700">{badgeCount[t.key]}</span> : null}
              </button>
            ))}
          </div>
          <div className="p-4 md:p-8 bg-white dark:bg-slate-800 flex-1 h-auto md:overflow-y-auto custom-scrollbar">
            {tab === 'info' && renderInfo()}
            {tab === 'extra' && renderExtra()}
            {tab === 'contracts' && renderContracts()}
            {tab === 'docs' && renderDocs()}
            {tab === 'assets' && renderAssets()}
            {tab === 'records' && renderRecords()}
            {tab === 'log' && renderLog()}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
      `,
      }} />

      {contractInit && <ContractModal empId={e.id} init={contractInit} onClose={() => setContractInit(null)} />}
      {evalFor && <EvalModal contractId={evalFor} onClose={() => setEvalFor(null)} onPassed={() => setContractInit({})} />}
      {offboard && <OffboardModal empId={e.id} defaultType={offboard} onClose={() => setOffboard(null)} />}
      {noShow && <NoShowModal empId={e.id} onClose={() => setNoShow(false)} onDone={cid => navigate(candUrl(cid))} />}
      {edit && <EditModal edit={edit} onClose={() => setEdit(null)} />}
      {move && <MoveModal e={e} initType={move} onClose={() => setMove(null)} />}
      {leaveOpen && <LeaveModal empId={e.id} onClose={() => setLeaveOpen(false)} />}
      {lic && (
        <Modal open title={lic.id ? 'Sửa giấy phép' : 'Thêm giấy phép / chứng chỉ'} onCancel={() => setLic(null)} okText="Lưu" cancelText="Đóng"
          onOk={() => {
            const r = hrApi.saveLicense(e.id, { kind: lic.kind, number: lic.number || '', issuedAt: lic.issuedAt || '', expiresAt: lic.expiresAt || undefined, scope: lic.scope, id: lic.id }, me);
            if (run(r, 'Đã lưu giấy phép')) setLic(null);
          }}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Loại" className="col-span-2">
              <Select className="w-full" value={lic.kind} onChange={kind => setLic({ ...lic, kind })} options={opts(LICENSE_KIND)} />
            </Field>
            <Field label="Số" className="col-span-2"><Input value={lic.number} onChange={x => setLic({ ...lic, number: x.target.value })} /></Field>
            <Field label="Ngày cấp"><DateInput value={lic.issuedAt} onChange={issuedAt => setLic({ ...lic, issuedAt })} /></Field>
            <Field label="Ngày hết hạn (nếu có)"><DateInput value={lic.expiresAt} onChange={expiresAt => setLic({ ...lic, expiresAt: expiresAt || undefined })} /></Field>
            <Field label="Phạm vi hành nghề" className="col-span-2"><Input value={lic.scope} onChange={x => setLic({ ...lic, scope: x.target.value })} /></Field>
          </div>
        </Modal>
      )}
      {pause && (
        <Modal open title="Tạm hoãn hợp đồng" onCancel={() => setPause(null)} okText="Lưu" cancelText="Đóng"
          onOk={() => { hrApi.pause(e.id, pause, me); message.success('Đã ghi nhận tạm hoãn'); setPause(null); }}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lý do" className="col-span-2">
              <Select className="w-full" value={pause.reason} onChange={reason => setPause({ ...pause, reason })}
                options={['Nghỉ thai sản', 'Nghỉ không lương', 'Điều trị bệnh dài ngày', 'Thực hiện nghĩa vụ quân sự'].map(x => ({ value: x, label: x }))} />
            </Field>
            <Field label="Từ ngày"><DateInput value={pause.from} onChange={from => setPause({ ...pause, from })} /></Field>
            <Field label="Đến ngày"><DateInput value={pause.to} onChange={to => setPause({ ...pause, to: to || undefined })} min={pause.from} /></Field>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

/* ----- Trường của các form danh sách ----- */
const DEP_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Họ và tên', span: true }, { key: 'relation', label: 'Quan hệ', type: 'select', options: DEP_RELATIONS.map(x => ({ value: x, label: x })) },
  { key: 'dob', label: 'Ngày sinh', type: 'date' }, { key: 'taxCode', label: 'MST người phụ thuộc', placeholder: '10 chữ số' },
  { key: 'from', label: 'Giảm trừ từ tháng', type: 'month' }, { key: 'to', label: 'Đến tháng (nếu thôi)', type: 'month' },
];
const SCHOOL_FIELDS: FieldDef[] = [
  { key: 'school', label: 'Trường học', span: true }, { key: 'place', label: 'Địa điểm' }, { key: 'degree', label: 'Bằng cấp' },
  { key: 'major', label: 'Chuyên ngành' }, { key: 'from', label: 'Từ năm' }, { key: 'to', label: 'Đến năm' },
  { key: 'highest', label: 'Bằng cấp cao nhất', type: 'check', placeholder: 'Đây là bằng cấp cao nhất' },
];
const WORK_FIELDS: FieldDef[] = [
  { key: 'company', label: 'Công ty', span: true }, { key: 'position', label: 'Vị trí', span: true }, { key: 'from', label: 'Từ năm' }, { key: 'to', label: 'Đến năm' },
  { key: 'note', label: 'Mô tả / lý do nghỉ', type: 'textarea' },
];
const ASSET_FIELDS: FieldDef[] = [
  { key: 'kind', label: 'Loại', type: 'select', options: opts(ASSET_KIND) }, { key: 'code', label: 'Mã tài sản' }, { key: 'name', label: 'Tên tài sản / thiết bị', span: true },
  { key: 'issuedAt', label: 'Ngày cấp', type: 'date' }, { key: 'value', label: 'Giá trị', type: 'money' },
  { key: 'condition', label: 'Tình trạng', type: 'select', options: ['Tốt', 'Trầy xước nhẹ', 'Hư hỏng nhẹ', 'Cần sửa chữa'].map(x => ({ value: x, label: x })) },
  { key: 'note', label: 'Ghi chú', type: 'textarea' },
];
const VIOLATION_FIELDS: FieldDef[] = [
  { key: 'title', label: 'Sự vụ', span: true }, { key: 'date', label: 'Ngày', type: 'date' },
  { key: 'level', label: 'Hình thức xử lý', type: 'select', options: DISCIPLINE_LEVELS.map(x => ({ value: x, label: x })) },
  { key: 'amount', label: 'Bồi thường (nếu có)', type: 'money' }, { key: 'note', label: 'Ghi chú', type: 'textarea' },
];
const REWARD_FIELDS: FieldDef[] = [
  { key: 'title', label: 'Nội dung khen thưởng', span: true }, { key: 'date', label: 'Ngày', type: 'date' }, { key: 'amount', label: 'Mức thưởng', type: 'money' },
  { key: 'note', label: 'Ghi chú', type: 'textarea' },
];

/** Dựng lại trang khi chuyển sang nhân sự khác */
export default function EmployeeDetail() {
  const [params] = useSearchParams();
  return <Detail key={params.get('id') || ''} />;
}

